import React, { createContext, useReducer, useCallback, useEffect } from 'react';
import _ from 'lodash';
import createDebug from 'debug';

import { SHAPE_KEYS } from './shapes';

const debug = createDebug('kaleidoscope:config');

export const ConfigContext = createContext({});

const SET_CONFIG = 'config/set';
const RESET_CONFIG = 'config/reset';

const LOCALSTORAGE_KEY = 'kaleidoscope:config';

const searchParams = new URLSearchParams(document.location.search);
const shadersEnabled = searchParams.get('shaders') === '1';

const defaultConfig = {
  shapes: {
    count: 20,
    boundary: 10,
    size: {
      min: 1,
      max: 3,
    },
    types: SHAPE_KEYS,
  },
  kaleidoscope: {
    enabled: true,
    sides: 6,
    angle: 0,
  },
  enableAutoRotation: false,
  enableWireframes: false,
  backgroundColor: '#171720',
  shaders: {
    enabled: false,
    glitch: false,
    afterimage: {
      enabled: false,
      damp: 0.9,
    },
    colorshift: {
      enabled: false,
      amount: 0.01,
      angle: 0,
    },
    bloom: {
      enabled: false,
      strength: 0.25,
      radius: 0,
      threshold: 0.5,
    },
    bokeh: {
      enabled: false,
      focus: 1,
      aperture: 1,
    },
  },
};

const getInitialConfig = () => {
  const value = localStorage.getItem(LOCALSTORAGE_KEY);
  if (value) {
    return JSON.parse(value);
  } else {
    return defaultConfig;
  }
};

const persistConfig = (config) => {
  localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(config));
  debug('persist');
};

export const configReducer = (state = defaultConfig, action) => {
  switch (action.type) {
    case SET_CONFIG:
      return _.set({ ...state }, action.key, action.value);
    case RESET_CONFIG:
      return defaultConfig;
    default:
      return state;
  }
};

export const ConfigProvider = (props) => {
  const [state, dispatch] = useReducer(configReducer, getInitialConfig());

  useEffect(() => {
    persistConfig(state);
  }, [state]);

  const useConfig = useCallback((key) => _.get(state, key), [state]);
  const setConfig = useCallback(
    (key, value) =>
      dispatch({
        type: SET_CONFIG,
        key,
        value,
      }),
    [dispatch],
  );
  const resetConfig = useCallback(() => dispatch({ type: RESET_CONFIG }), [dispatch]);

  // TODO move this ?
  useEffect(() => {
    setConfig('shaders.enabled', shadersEnabled);
  }, []);

  const context = {
    useConfig,
    setConfig,
    resetConfig,
  };

  window.resetConfig = resetConfig;

  return <ConfigContext.Provider value={context}>{props.children}</ConfigContext.Provider>;
};
